---
title: 'Why We Chose Bare Metal Kubernetes for Web3 Infrastructure'
slug: 'bare-metal-kubernetes-hosting'
date: '2026-04-07'
description: 'How LinkPool runs high-performance Kubernetes clusters on bare metal servers across two Tier 4 data centres, and why cloud was never an option for mission-critical Web3 workloads.'
heroImage: './hero.png'
author: 'LinkPool Team'
---

When you are running infrastructure that secures hundreds of millions of dollars in onchain assets, every millisecond of latency and every layer of abstraction matters. That is why LinkPool runs its entire stack on bare metal Kubernetes — no cloud providers, no shared tenancy, no compromises.

## The Problem with Cloud for Web3

Cloud platforms like AWS, GCP, and Azure are excellent for many use cases. But for mission-critical Web3 infrastructure, they introduce risks that are difficult to accept:

- **Noisy neighbours** — shared hardware means unpredictable performance spikes
- **Latency overhead** — virtualisation layers add milliseconds that compound across thousands of daily transactions
- **Vendor lock-in** — dependence on a single provider creates a centralisation risk that contradicts the ethos of decentralised infrastructure
- **Cost at scale** — cloud pricing becomes prohibitive when you are processing billions of requests per month

> Running Web3 infrastructure on centralised cloud providers is an irony we chose not to live with.

## Our Bare Metal Setup

LinkPool operates out of two **Tier 4 data centres** — the highest classification for uptime and redundancy. Here is what that means in practice:

### Hardware

Every server in our fleet is purpose-built for its workload:

- **Validator nodes** run on dedicated machines with ECC memory, NVMe storage, and redundant power supplies
- **RPC endpoints** are backed by high-core-count processors optimised for concurrent request handling
- **Storage nodes** use enterprise-grade SSDs in RAID configurations with hot-swap capability

We do not share hardware with anyone. Every rack, every switch, every cable is ours.

### Network

Our network architecture is designed for both performance and resilience:

1. **Dual uplinks** from independent transit providers at each data centre
2. **Sub-1ms inter-rack latency** within each facility
3. **Dedicated 10Gbps links** between our two data centres for real-time replication
4. **BGP peering** with major exchanges for optimal routing to blockchain networks worldwide

### Power and Cooling

Tier 4 means **2N redundancy** — every critical system has a fully independent backup:

- Dual power feeds from separate utility substations
- N+1 UPS systems with diesel generator backup
- Precision cooling with redundant CRAC units
- 99.995% guaranteed uptime SLA

## Why Kubernetes on Bare Metal

Running Kubernetes without a cloud provider sounds daunting, but it gives us capabilities that managed Kubernetes services cannot match.

### Full Control Over Scheduling

We control exactly which workloads run on which hardware. Validator nodes get dedicated machines — they never compete for resources with other services. This eliminates the performance variance that plagues cloud-hosted validators.

### Custom Networking

Without cloud networking abstractions, we can optimise packet paths directly. Our CNI configuration is tuned for minimum-latency pod-to-pod communication, and we use SR-IOV for workloads that need direct NIC access.

### No Abstraction Tax

Every layer of virtualisation adds overhead. On bare metal, our containers talk directly to the kernel, which talks directly to the hardware. The result is measurably lower latency and higher throughput compared to equivalent cloud instances.

### Hardware-Level Security

We control the full stack from BIOS to application. This means:

- **Secure boot chains** verified at every level
- **Hardware security modules** for key management
- **Physical security** with biometric access controls and 24/7 monitoring
- No third-party hypervisors or firmware we did not choose

## The Numbers Speak

Since moving to our current bare metal Kubernetes setup, we have seen:

- **40% reduction in p99 latency** compared to our previous cloud deployment
- **99.95% uptime** across all services over the past 12 months
- **3x cost reduction** per request at our current scale
- **Zero unplanned failovers** between data centres

These improvements directly translate to better service for the protocols and enterprises that depend on our infrastructure.

## Operational Challenges

Running bare metal is not without its challenges. Here is what we have learned:

### Capacity Planning

There is no auto-scale button. We forecast demand months in advance, order hardware, rack it, burn it in, and integrate it into our clusters. This requires discipline but also means we are never surprised by a cloud provider's capacity limits.

### Upgrades and Maintenance

Rolling upgrades on bare metal require careful orchestration. We have built tooling that can drain nodes, update firmware, upgrade the OS, and return nodes to service with zero downtime. Every maintenance window is rehearsed before execution.

### Monitoring and Alerting

When you own the hardware, you own every failure mode. Our monitoring stack tracks everything from CPU temperature and disk SMART data to kernel metrics and application-level SLOs. We detect hardware degradation before it becomes a failure.

## Looking Ahead

We are continuously investing in our infrastructure. Current projects include:

- **GPU nodes** for emerging AI-powered oracle use cases
- **Expanded edge presence** to reduce latency for Asian and South American networks
- **Next-generation storage architecture** for historical blockchain data at petabyte scale

Our commitment to bare metal is not going to change. For the kind of infrastructure that Web3 demands — reliable, performant, and truly decentralised — there is no substitute for owning every layer of the stack.

---

If you are building a protocol or enterprise application that needs infrastructure you can count on, we would love to talk. Reach out through our [website](https://linkpool.com) or find us on [Twitter](https://twitter.com/linkpoolio).
